import React, { useState, useEffect } from "react";
import {
  PulseButton,
  PulseTextField,
  PulseButtonTheme,
  PulseSpinnerIcon,
} from "@pulselabs-ai/shared-fe-ui";
// import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import MfaAppService from "../../../services/MfaAppService";
import { FlightRecorderProjectDTO } from "../../../types/flight-recorder.types";
import "./AppInfoContent.scss";
import { UploadIcon } from "../../../components/icons";

interface AppInfoContentProps {
  projectId: string;
}

const AppInfoContent: React.FC<AppInfoContentProps> = ({ projectId }) => {
  const [project, setProject] = useState<FlightRecorderProjectDTO | null>(null);
  const [appName, setAppName] = useState("");
  const [appObjective, setAppObjective] = useState("");
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appIcon, setAppIcon] = useState<string | null>(null);
  const { user } = useSelector((state: RootState) => state.user);
  const activeWorkspace = useSelector(
    (state: RootState) => state.user.user?.defaultWorkspace,
  );

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const mfaAppService = MfaAppService.getInstance();
        const fetchedProject = await mfaAppService.getProject(
          projectId,
          user?.id?.toString() || "",
        );
        setProject(fetchedProject);
        setAppName(fetchedProject.project.projectName);
        setAppObjective(fetchedProject.project.projectObjective);
        // If project has an icon in metadata, set it
        // if (fetchedProject.project.projectMetadata?.iconUrl) {
        //   setAppIcon(fetchedProject.project.projectMetadata.iconUrl);
        // }
      } catch (err) {
        console.error("Failed to fetch project:", err);
        setError("Failed to load project information");
      }
    };
    fetchProject();
  }, [projectId, user?.id]);

  const handleSave = async () => {
    if (!projectId || !user?.id || !activeWorkspace?.id) return;

    try {
      setSaving(true);
      setError(null);

      console.log("isSaving", isSaving);

      const mfaAppService = MfaAppService.getInstance();
      const projectInput = {
        projectName: appName,
        projectObjective: appObjective,
        workspaceId: activeWorkspace.id.toString(),
        userId: user.id.toString(),
        status: "active",
        projectMetadata: {
          ...(project?.project.projectMetadata || {}),
          iconUrl: appIcon,
        },
      };

      await mfaAppService.updateProject(projectId, projectInput);

      // Refresh project data
      const fetchedProject = await mfaAppService.getProject(
        projectId,
        user.id.toString(),
      );
      setProject(fetchedProject);
    } catch (err) {
      console.error("Failed to update project:", err);
      setError("Failed to update project information");
    } finally {
      setSaving(false);
    }
  };

  const handleAppIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Only allow images
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setAppIcon(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setAppIcon(null);
  };

  const isValid =
    appName.trim() &&
    appObjective.trim() &&
    appName.length <= 20 &&
    appObjective.length <= 80;

  useEffect(() => {
    // Auto-save when valid changes are made
    if (isValid && project) {
      const timer = setTimeout(() => {
        handleSave();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [appName, appObjective, appIcon]);

  if (!project) {
    return (
      <div className="app-info-spinner">
        <PulseSpinnerIcon />
      </div>
    );
  }

  return (
    <div className="app-info-content">
      <div className="headline-xs">App Info</div>

      {/* <div className="app-info-form"> */}
      {error && <div className="error-message">{error}</div>}

      <div className="form-field">
        <PulseTextField
          label="Name"
          isHideIcon={true}
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          placeholder="Enter app name"
          helperText="Max 20 characters"
          style={{ width: "100% !important" }}
        />
      </div>

      <div className="form-field">
        <PulseTextField
          label="Objective"
          isHideIcon={true}
          value={appObjective}
          onChange={(e) => setAppObjective(e.target.value)}
          placeholder="Enter app objective"
          multiline
          rows={3}
          helperText="80 characters maximum"
          style={{ width: "100% !important" }}
        />
      </div>

      <div className="form-field icon-field">
        {appIcon && (
          <div className="app-icon-preview">
            <img src={appIcon} alt="App icon" />
          </div>
        )}

        <div className="icon-actions">
          <label className="upload-button" htmlFor="icon-upload">
            <input
              id="icon-upload"
              type="file"
              accept="image/*"
              onChange={handleAppIconUpload}
              style={{ display: "none" }}
            />
            <PulseButton
              theme={PulseButtonTheme.SECONDARY}
              startIcon={<UploadIcon />}
              component="span"
            >
              UPLOAD APP ICON
            </PulseButton>
          </label>

          {appIcon && (
            <PulseButton
              theme={PulseButtonTheme.SECONDARY}
              onClick={handleRemoveIcon}
            >
              REMOVE APP ICON
            </PulseButton>
          )}
        </div>
      </div>
    </div>
    // </div>
  );
};

export default AppInfoContent;
